"use client";

import { useEffect, useState } from "react";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "@repo/backend/convex/_generated/api";
import type { Id } from "@repo/backend/convex/_generated/dataModel";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { useBoard } from "@/components/BoardProvider";
import { ProofRedactor } from "@/components/scholarship/ProofRedactor";
import { SchoolCombobox } from "@/components/scholarship/SchoolCombobox";
import { captureClientEvent } from "@/lib/analytics/client";
import { cn } from "@/lib/utils";

type EvidenceType =
  | "free_school_meals"
  | "bursary_16_19"
  | "care_leaver"
  | "universal_credit"
  | "tax_credits"
  | "income_support"
  | "asylum_support"
  | "pension_credit";

// Evidence wording follows the official UAT bursary scheme so a student can
// reuse the exact document they already sent there.
const EVIDENCE_OPTIONS: {
  value: EvidenceType;
  label: string;
  proof: string;
}[] = [
  {
    value: "free_school_meals",
    label: "Free School Meals",
    proof: "A letter from your school, this school year.",
  },
  {
    value: "bursary_16_19",
    label: "16-19 Bursary or EMA",
    proof: "Your award letter, this school year.",
  },
  {
    value: "care_leaver",
    label: "Care leaver support",
    proof: "A letter from your local authority.",
  },
  {
    value: "universal_credit",
    label: "Universal Credit",
    proof: "A statement from the last three months.",
  },
  {
    value: "tax_credits",
    label: "Working or Child Tax Credit",
    proof: "An award letter, this school year.",
  },
  {
    value: "income_support",
    label: "Income Support, JSA or ESA",
    proof: "A Jobcentre Plus letter from this year.",
  },
  {
    value: "asylum_support",
    label: "Immigration or asylum support",
    proof: "A letter from the last 12 months.",
  },
  {
    value: "pension_credit",
    label: "State Pension Credit",
    proof: "A letter from the last 12 months.",
  },
];

const PATHS: { evidence: boolean; title: string; body: string }[] = [
  {
    evidence: true,
    title: "I'm on a qualifying benefit",
    body: "Free School Meals, Universal Credit and more. You'll show a letter.",
  },
  {
    evidence: false,
    title: "I don't have any of these",
    body: "Not on these benefits, or can't get a letter. Apply on your circumstances alone.",
  },
];

const STATUS_COPY: Record<string, string> = {
  new: "It's with us. We'll email you within five working days.",
  reviewing:
    "We're reading it now. You'll get an email as soon as it's decided.",
  approved: "Approved.",
  declined:
    "We couldn't award one this time. Everything free on the site stays free, and you can reply to our email if things change.",
};

const primaryButton =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-6 py-3 font-polysans text-sm font-medium text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none";

const ghostButton =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full border border-border px-6 py-3 font-polysans text-sm font-medium transition-colors hover:border-foreground/30 disabled:pointer-events-none disabled:opacity-40";

// Underlined, transparent fields: the page background carries the form rather
// than a card, so nothing here uses a filled input box.
const field =
  "w-full border-0 border-b border-border bg-transparent pb-2.5 font-polysans text-lg font-light text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground";

function Question({
  n,
  label,
  hint,
  children,
}: {
  n: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 border-t border-border pt-8 first:border-t-0 first:pt-0 sm:gap-6">
      <span className="w-6 shrink-0 pt-1 font-script text-xl leading-none text-primary">
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-polysans text-base font-medium">{label}</p>
        {hint ? (
          <p className="mt-1 font-polysans text-sm font-light text-muted-foreground">
            {hint}
          </p>
        ) : null}
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Word of mouth is the only distribution this scheme has, so an award is the
 * moment to ask for a post. Shown after the decision, never before, so nobody
 * can read it as a condition of being funded.
 */
function ShareAsk({
  percent,
  examShortName,
}: {
  percent: number;
  examShortName: string;
}) {
  const [copied, setCopied] = useState(false);
  const site = typeof window === "undefined" ? "" : window.location.host;
  const url =
    typeof window === "undefined"
      ? ""
      : `${window.location.origin}/scholarship`;
  const text = `I applied for a ${site} scholarship for my ${examShortName} prep and got ${percent}% off. If you're sitting it and the price is the problem, it's worth an application.`;
  const pill = cn(ghostButton, "px-5 py-2.5 text-xs");

  return (
    <div className="mx-auto mt-14 max-w-md border-t border-border pt-8">
      <p className="font-polysans text-sm font-light text-muted-foreground">
        Post about it on Linkedin and tag us. Send the link to anyone who needs
        it.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <a
          className={pill}
          // The share-offsite endpoint posts a bare link with no words, so this
          // opens the feed composer prefilled instead and lets LinkedIn unfurl
          // the URL sitting in the text.
          href={`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${text} ${url}`)}`}
          target="_blank"
          rel="noreferrer"
        >
          Linkedin
        </a>
        <a
          className={pill}
          href={`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noreferrer"
        >
          X
        </a>
        <a
          className={pill}
          href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
          target="_blank"
          rel="noreferrer"
        >
          WhatsApp
        </a>
        <button
          type="button"
          className={pill}
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? "Link copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

export function ScholarshipApplication() {
  const board = useBoard();
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const { openAuthModal } = useAuthModal();

  const state = useQuery(
    api.scholarships.getMyScholarship,
    isAuthenticated ? {} : "skip",
  );
  const sendCode = useMutation(api.scholarships.sendSchoolEmailCode);
  const verifyCode = useMutation(api.scholarships.verifySchoolEmailCode);
  const generateUploadUrl = useMutation(
    api.scholarships.generateProofUploadUrl,
  );
  const apply = useMutation(api.scholarships.apply);
  const saveDraft = useMutation(api.scholarships.saveDraft);

  const [schoolEmail, setSchoolEmail] = useState("");
  const [code, setCode] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [evidenceTypes, setEvidenceTypes] = useState<EvidenceType[]>([]);
  const [skipEvidence, setSkipEvidence] = useState(false);
  const [blockers, setBlockers] = useState("");
  const [ukResident, setUkResident] = useState(false);
  const [inFullTimeEducation, setInFullTimeEducation] = useState(false);
  const [universities, setUniversities] = useState("");
  const [predictedGrades, setPredictedGrades] = useState("");
  const [proof, setProof] = useState<Blob | null>(null);
  const [changingEmail, setChangingEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a record of people who start the form and don't submit. Fires once the
  // email is verified, debounced, and only when something's actually been
  // typed so a fresh page load can't blank an earlier draft.
  const verified = Boolean(state?.verifiedEmail);
  const alreadyApplied = Boolean(state?.application);
  useEffect(() => {
    if (!verified || alreadyApplied) return;
    const touched =
      schoolName ||
      evidenceTypes.length > 0 ||
      blockers ||
      universities ||
      predictedGrades;
    if (!touched) return;
    const t = setTimeout(() => {
      void saveDraft({
        schoolName,
        evidenceTypes,
        blockers,
        ukResident,
        inFullTimeEducation,
        universities,
        predictedGrades,
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [
    verified,
    alreadyApplied,
    schoolName,
    evidenceTypes,
    blockers,
    ukResident,
    inFullTimeEducation,
    universities,
    predictedGrades,
    saveDraft,
  ]);

  // Whether they're showing benefit paperwork. Everyone writes why they need it
  // either way: on this path it's what the award tier is judged on, on the other
  // it's the whole case.
  const usingEvidence = !skipEvidence;

  // Only the messages the backend deliberately raised are worth showing; an
  // unexpected failure arrives as an opaque Error and gets the generic line.
  const message = (e: unknown) =>
    e instanceof ConvexError && typeof e.data === "string"
      ? e.data
      : "Something went wrong at our end. Try again in a moment.";

  if (authLoading) return null;

  if (!isAuthenticated) {
    return (
      <div className="text-center">
        <h2 className="font-script text-3xl">Sign in to apply</h2>
        <p className="mx-auto mt-3 max-w-md font-polysans text-base font-light text-muted-foreground">
          An award lands straight on your account, so you need one first.
          It&apos;s free.
        </p>
        <button
          type="button"
          className={cn(primaryButton, "mt-7")}
          onClick={() =>
            openAuthModal("signup", { redirectTo: "/scholarship" })
          }
        >
          Create an account →
        </button>
      </div>
    );
  }

  if (state === undefined) return null;

  if (state.application) {
    const { status, awardPercent } = state.application;
    return (
      <div className="text-center">
        <h2 className="font-script text-3xl md:text-4xl">
          {status === "approved" && awardPercent
            ? `You've got a ${awardPercent}% scholarship`
            : "Your application is in"}
        </h2>
        <p className="mx-auto mt-3 max-w-md font-polysans text-base font-light text-muted-foreground">
          {status === "approved" && awardPercent
            ? awardPercent === 100
              ? "Full access is already on your account. Nothing to pay."
              : "The discount is on your account and comes off at checkout."
            : STATUS_COPY[status]}
        </p>
        {status === "approved" && awardPercent !== 100 ? (
          <a href="/pricing" className={cn(primaryButton, "mt-7")}>
            Choose a pass →
          </a>
        ) : null}
        {status === "approved" && awardPercent ? (
          <ShareAsk percent={awardPercent} examShortName={board.shortName} />
        ) : null}
      </div>
    );
  }

  const verifiedEmail = state.verifiedEmail;

  const onSendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await sendCode({ schoolEmail });
      setChangingEmail(false);
      setCode("");
      captureClientEvent("scholarship_code_requested", {});
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (value: string) => {
    if (value.length < 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await verifyCode({ code: value });
    } catch (e) {
      setError(message(e));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const toggleEvidence = (value: EvidenceType) =>
    setEvidenceTypes((current) =>
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    );

  const onSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      let proofStorageId: Id<"_storage"> | undefined;
      if (usingEvidence && proof) {
        const uploadUrl = await generateUploadUrl({});
        const res = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": "image/jpeg" },
          body: proof,
        });
        if (!res.ok) {
          setError(
            "Your proof didn't upload. Check your connection and try again.",
          );
          return;
        }
        proofStorageId = ((await res.json()) as { storageId: Id<"_storage"> })
          .storageId;
      }
      await apply({
        schoolName,
        evidenceTypes: usingEvidence ? evidenceTypes : [],
        blockers: blockers.trim(),
        ukResident,
        inFullTimeEducation,
        universities,
        predictedGrades,
        proofStorageId,
      });
      captureClientEvent("scholarship_application_submitted", {
        evidenceTypes: usingEvidence ? evidenceTypes.join(",") : "blockers",
      });
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  // Step 1: prove the school address is theirs before we take any benefit data.
  if (!verifiedEmail) {
    const awaitingCode = changingEmail ? null : state.codeSentTo;

    // Once the code is out, the address step collapses to a line of text: the
    // code is the only thing left to do, so it gets the whole screen.
    if (awaitingCode) {
      return (
        <div className="text-center">
          <h2 className="font-script text-3xl md:text-4xl">
            Check your school inbox
          </h2>
          <p className="mx-auto mt-3 max-w-md font-polysans text-base font-light text-muted-foreground">
            We sent a six-digit code to{" "}
            <span className="text-foreground">{awaitingCode}</span>.
          </p>

          <div className="mt-10 flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={(v) => {
                setCode(v);
                setError(null);
              }}
              onComplete={onVerify}
              disabled={busy}
              containerClassName="justify-center"
            >
              <InputOTPGroup className="gap-2 sm:gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-14 w-11 rounded-xl! border! border-border font-polysans text-xl shadow-none data-[active=true]:border-primary data-[active=true]:ring-primary/20 sm:h-16 sm:w-13 sm:text-2xl"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {error ? (
            <p className="mt-5 font-polysans text-sm text-destructive">
              {error}
            </p>
          ) : (
            <p className="mt-5 font-polysans text-sm font-light text-muted-foreground">
              {busy ? "Checking…" : "It expires in 15 minutes."}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-polysans text-sm font-light">
            <button
              type="button"
              className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={onSendCode}
              disabled={busy}
            >
              Send it again
            </button>
            <button
              type="button"
              className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => {
                setSchoolEmail(awaitingCode);
                setCode("");
                setError(null);
                setChangingEmail(true);
              }}
            >
              Use a different address
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center">
        <h2 className="font-script text-3xl md:text-4xl">
          Start with your school email
        </h2>
        <p className="mx-auto mt-3 max-w-md font-polysans text-base font-light text-muted-foreground">
          We&apos;ll send a six-digit code to check it&apos;s yours.
        </p>

        <div className="mx-auto mt-10 flex max-w-md flex-col gap-4 sm:flex-row sm:items-end">
          <input
            id="school-email"
            type="email"
            autoComplete="off"
            className={cn(field, "text-center sm:text-left")}
            value={schoolEmail}
            onChange={(e) => setSchoolEmail(e.target.value)}
            placeholder="you@yourschool.sch.uk"
          />
          <button
            type="button"
            className={cn(primaryButton, "shrink-0")}
            onClick={onSendCode}
            disabled={busy || !schoolEmail}
          >
            Send code →
          </button>
        </div>

        {error ? (
          <p className="mx-auto mt-5 max-w-md font-polysans text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  const canSubmit =
    (!usingEvidence || (evidenceTypes.length > 0 && Boolean(proof))) &&
    blockers.trim().length > 0 &&
    ukResident &&
    inFullTimeEducation &&
    schoolName.trim().length > 0 &&
    universities.trim().length > 0 &&
    predictedGrades.trim().length > 0;

  // Step 2: the application itself.
  return (
    <div>
      <div className="text-center">
        <h2 className="font-script text-3xl md:text-4xl">Your application</h2>
        <p className="mt-2 font-polysans text-sm font-light text-muted-foreground">
          Verified as {verifiedEmail}
        </p>
      </div>

      <div className="mt-12 space-y-10">
        <Question
          n="01"
          label="Where are you studying?"
          hint="Start typing and pick your school from the list."
        >
          <SchoolCombobox
            id="school-name"
            className={field}
            value={schoolName}
            onChange={setSchoolName}
            placeholder="e.g. Hills Road Sixth Form College"
          />
        </Question>

        <Question n="02" label="Are you on a qualifying benefit?">
          <div className="space-y-6">
            <div className="grid gap-2 sm:grid-cols-2">
              {PATHS.map((p) => {
                const selected = usingEvidence === p.evidence;
                return (
                  <button
                    key={p.title}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSkipEvidence(!p.evidence)}
                    className={cn(
                      "flex items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors",
                      selected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-foreground/25",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors",
                        selected ? "border-primary" : "border-border",
                      )}
                    >
                      {selected ? (
                        <span className="size-2 rounded-full bg-primary" />
                      ) : null}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-polysans text-sm font-medium">
                        {p.title}
                      </span>
                      <span className="mt-0.5 block font-polysans text-xs font-light text-muted-foreground">
                        {p.body}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {usingEvidence ? (
              <div className="space-y-6">
                <div className="grid gap-2 sm:grid-cols-2">
                  {EVIDENCE_OPTIONS.map((o) => {
                    const selected = evidenceTypes.includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => toggleEvidence(o.value)}
                        className={cn(
                          "flex items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors",
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-foreground/25",
                        )}
                      >
                        <span
                          className={cn(
                            "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors",
                            selected
                              ? "border-primary bg-primary text-white"
                              : "border-border",
                          )}
                        >
                          {selected ? (
                            <svg
                              viewBox="0 0 12 12"
                              className="size-3"
                              aria-hidden="true"
                            >
                              <path
                                d="M2.5 6.2 4.8 8.5 9.5 3.8"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-polysans text-sm font-medium">
                            {o.label}
                          </span>
                          <span className="mt-0.5 block font-polysans text-xs font-light text-muted-foreground">
                            {o.proof}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <ProofRedactor onChange={setProof} />
              </div>
            ) : null}
          </div>
        </Question>

        <Question
          n="03"
          label="Why do you need a scholarship?"
          hint="What's making it hard to afford the prep right now?"
        >
          <textarea
            id="blockers"
            rows={5}
            aria-label="Why do you need a scholarship?"
            className="w-full resize-none rounded-2xl border border-border bg-transparent px-4 py-3 font-polysans text-base font-light text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground"
            placeholder="Write as much as you want."
            value={blockers}
            onChange={(e) => setBlockers(e.target.value)}
          />
        </Question>

        <Question n="04" label="Where are you applying, and for what?">
          <input
            id="universities"
            className={field}
            value={universities}
            onChange={(e) => setUniversities(e.target.value)}
            placeholder="Cambridge and Imperial for Engineering"
          />
        </Question>

        <Question n="05" label="Predicted grades">
          <input
            id="grades"
            className={field}
            value={predictedGrades}
            onChange={(e) => setPredictedGrades(e.target.value)}
            placeholder="A*A*A in Maths, Further Maths, Physics"
          />
        </Question>

        <Question n="06" label="Two last things">
          <div className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3 font-polysans text-sm font-light">
              <Checkbox
                className="mt-0.5"
                checked={ukResident}
                onCheckedChange={(c) => setUkResident(Boolean(c))}
              />
              <span>
                I live in the UK and will sit the {board.shortName} here.
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 font-polysans text-sm font-light">
              <Checkbox
                className="mt-0.5"
                checked={inFullTimeEducation}
                onCheckedChange={(c) => setInFullTimeEducation(Boolean(c))}
              />
              <span>
                I&apos;m under 21 and in full-time education, or over 21 and on
                a qualifying benefit myself.
              </span>
            </label>
          </div>
        </Question>
      </div>

      {error ? (
        <p className="mt-6 font-polysans text-sm text-destructive">{error}</p>
      ) : null}

      <div className="mt-12 text-center">
        <button
          type="button"
          className={cn(primaryButton, "px-10 py-4 text-base")}
          onClick={onSubmit}
          disabled={busy || !canSubmit}
        >
          Send my application →
        </button>
        <p className="mt-4 font-polysans text-xs font-light text-muted-foreground">
          You&apos;ll hear from us by email either way. Your proof is deleted
          the moment we decide.
        </p>
      </div>
    </div>
  );
}
